export type HubMode =
  | 'oauth'
  | 'messaging'
  | 'api_key'
  | 'mcp_hosted'
  | 'mcp_stdio'
  | 'openapi';

export type HubStatus = 'connected' | 'error';

export interface ParsedHubParams {
  open: boolean;
  brand?: string;
  mode?: HubMode;
  status?: HubStatus;
  errorCode?: string;
  customMcpServerName?: string;
}

const VALID_MODES = new Set<HubMode>([
  'oauth',
  'messaging',
  'api_key',
  'mcp_hosted',
  'mcp_stdio',
  'openapi',
]);

function asMode(value: string | null): HubMode | undefined {
  return value && VALID_MODES.has(value as HubMode) ? (value as HubMode) : undefined;
}

function mcpBrand(value: string | null): string | undefined {
  return value?.replace(/^mcp:/, '').replace(/-(admin|storefront)$/, '') || undefined;
}

export function parseHubParams(search: string | URLSearchParams): ParsedHubParams {
  const sp = typeof search === 'string' ? new URLSearchParams(search) : search;

  const explicitHub = sp.get('hub') === '1';
  const hubBrand = sp.get('hubBrand') || undefined;
  const hubMode = asMode(sp.get('hubMode'));
  const hubStatusRaw = sp.get('hubStatus');
  const hubStatus =
    hubStatusRaw === 'connected' || hubStatusRaw === 'error'
      ? hubStatusRaw
      : undefined;

  const mcpConnected = sp.get('mcp_connected');
  const mcpError = sp.get('mcp_error');
  const openapiConnected = sp.get('openapi_connected');
  const mcpConnect = sp.get('mcpConnect');
  const openapiConnect = sp.get('openapiConnect');
  const integrationType = sp.get('integrationType');
  const integrationProvider = sp.get('integrationProvider');
  const mcpServerName = sp.get('mcpServerName');

  if (explicitHub || hubBrand || hubMode || hubStatus) {
    return {
      open: true,
      brand: hubBrand,
      mode: hubMode,
      status: hubStatus,
      errorCode: hubStatus === 'error' ? mcpError ?? undefined : undefined,
      customMcpServerName: mcpServerName ?? undefined,
    };
  }

  if (mcpConnected) {
    return {
      open: true,
      brand: mcpBrand(mcpConnected),
      mode: 'mcp_hosted',
      status: 'connected',
      customMcpServerName: mcpConnected,
    };
  }

  if (mcpError) {
    return {
      open: true,
      brand: mcpBrand(mcpConnect ?? mcpServerName),
      mode: 'mcp_hosted',
      status: 'error',
      errorCode: mcpError,
      customMcpServerName: mcpServerName ?? undefined,
    };
  }

  if (openapiConnected) {
    return {
      open: true,
      brand: 'custom_openapi',
      mode: 'openapi',
      status: 'connected',
      customMcpServerName: openapiConnected,
    };
  }

  if (mcpConnect || mcpServerName) {
    return {
      open: true,
      brand: mcpBrand(mcpConnect ?? mcpServerName),
      mode: 'mcp_hosted',
      customMcpServerName: mcpServerName ?? undefined,
    };
  }

  if (openapiConnect) {
    return {
      open: true,
      brand: 'custom_openapi',
      mode: 'openapi',
      customMcpServerName: openapiConnect,
    };
  }

  if (integrationType && integrationProvider) {
    return {
      open: true,
      brand: integrationProvider,
      mode:
        integrationType === 'messaging'
          ? 'messaging'
          : integrationType === 'media_gen'
            ? 'api_key'
            : 'oauth',
    };
  }

  if (sp.get('integrations') === '1') {
    return { open: true };
  }

  return { open: false };
}
